<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import paymentService from '../services/payment.service'
import paymentTemplateService from '../services/paymentTemplate.service'
import SendPaymentReminderModal from '../components/SendPaymentReminderModal.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check if user has permission
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canViewPayments = computed(() => {
  return hasPermission('PAYMENT_INDEX')
})

const canCreatePayments = computed(() => {
  return hasPermission('PAYMENT_STORE')
})

const canSendReminders = computed(() => {
  return hasPermission('REMINDER_SENT')
})

// Data
const payments = ref<any[]>([])
const loading = ref(false)
const pagination = ref({
  currentPage: 1,
  perPage: 50,
  total: 0,
  lastPage: 1,
  from: 0,
  to: 0
})

// Filters
const filters = ref({
  search: '',
  status: '',
  payment_date_from: '',
  payment_date_to: '',
  due_date_from: '',
  due_date_to: ''
})

const showFilterModal = ref(false)
const showDeleteModal = ref(false)
const showMarkAsPaidModal = ref(false)
const showSendReminderModal = ref(false)
const paymentToDelete = ref<any>(null)
const paymentToMarkAsPaid = ref<any>(null)
const paymentReminderTemplate = ref<string>('')
const singlePaymentForReminder = ref<string | null>(null)
const expandedPayments = ref<Set<string>>(new Set())
const selectedPayments = ref<Set<string>>(new Set())
const selectAll = ref(false)
const showBulkActions = computed(() => selectedPayments.value.size > 0)

// Initialize filters from URL query params
const initializeFiltersFromUrl = () => {
  filters.value.search = (route.query.search as string) || ''
  filters.value.status = (route.query.status as string) || ''
  filters.value.payment_date_from = (route.query.payment_date_from as string) || ''
  filters.value.payment_date_to = (route.query.payment_date_to as string) || ''
  filters.value.due_date_from = (route.query.due_date_from as string) || ''
  filters.value.due_date_to = (route.query.due_date_to as string) || ''
  pagination.value.currentPage = parseInt((route.query.page as string) || '1')
}

// Fetch payments
const fetchPayments = async () => {
  if (!clubUuid.value || !canViewPayments.value) return

  loading.value = true
  try {
    const params: any = {
      page: pagination.value.currentPage,
      per_page: pagination.value.perPage
    }

    // Add filters to params
    if (filters.value.search) params.search = filters.value.search
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.payment_date_from) params.payment_date_from = filters.value.payment_date_from
    if (filters.value.payment_date_to) params.payment_date_to = filters.value.payment_date_to
    if (filters.value.due_date_from) params.due_date_from = filters.value.due_date_from
    if (filters.value.due_date_to) params.due_date_to = filters.value.due_date_to

    const response = await paymentService.getPayments(clubUuid.value, params)

    payments.value = response.data || []
    pagination.value = {
      currentPage: response.current_page || 1,
      perPage: response.per_page || 15,
      total: response.total || 0,
      lastPage: response.last_page || 1,
      from: response.from || 0,
      to: response.to || 0
    }
  } catch (error: any) {
    console.error('Error fetching payments:', error)
    toastStore.error(t('paymentsList.errorFetchingPayments'))
  } finally {
    loading.value = false
  }
}

// Update URL with filters
const updateUrl = () => {
  const query: any = {}

  if (filters.value.search) query.search = filters.value.search
  if (filters.value.status) query.status = filters.value.status
  if (filters.value.payment_date_from) query.payment_date_from = filters.value.payment_date_from
  if (filters.value.payment_date_to) query.payment_date_to = filters.value.payment_date_to
  if (filters.value.due_date_from) query.due_date_from = filters.value.due_date_from
  if (filters.value.due_date_to) query.due_date_to = filters.value.due_date_to
  if (pagination.value.currentPage > 1) query.page = pagination.value.currentPage

  router.replace({ query })
}

// Apply filters
const applyFilters = () => {
  pagination.value.currentPage = 1
  updateUrl()
  fetchPayments()
  showFilterModal.value = false
}

// Clear all filters
const clearAllFilters = () => {
  filters.value = {
    search: '',
    status: '',
    payment_date_from: '',
    payment_date_to: '',
    due_date_from: '',
    due_date_to: ''
  }
  pagination.value.currentPage = 1
  updateUrl()
  fetchPayments()
}

// Remove single filter
const removeFilter = (filterKey: string) => {
  filters.value[filterKey as keyof typeof filters.value] = ''
  pagination.value.currentPage = 1
  updateUrl()
  fetchPayments()
}

// Check if any filter is active
const hasActiveFilters = computed(() => {
  return filters.value.search !== '' ||
         filters.value.status !== '' ||
         filters.value.payment_date_from !== '' ||
         filters.value.payment_date_to !== '' ||
         filters.value.due_date_from !== '' ||
         filters.value.due_date_to !== ''
})

// Pagination handlers
const changePage = (page: number) => {
  pagination.value.currentPage = page
  updateUrl()
  fetchPayments()
}

const prevPage = () => {
  if (pagination.value.currentPage > 1) {
    changePage(pagination.value.currentPage - 1)
  }
}

const nextPage = () => {
  if (pagination.value.currentPage < pagination.value.lastPage) {
    changePage(pagination.value.currentPage + 1)
  }
}

const changePerPage = () => {
  pagination.value.currentPage = 1
  updateUrl()
  fetchPayments()
}

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, pagination.value.currentPage - Math.floor(maxVisible / 2))
  let end = Math.min(pagination.value.lastPage, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Toggle payment accordion (mobile)
const togglePaymentAccordion = (paymentUuid: string) => {
  if (expandedPayments.value.has(paymentUuid)) {
    expandedPayments.value.delete(paymentUuid)
  } else {
    expandedPayments.value.add(paymentUuid)
  }
}

const isPaymentExpanded = (paymentUuid: string) => {
  return expandedPayments.value.has(paymentUuid)
}

// Format date (YYYY-MM-DD)
const formatDate = (dateString: string) => {
  if (!dateString) return '-'
  return dateString.split('T')[0]
}

// Format datetime (YYYY-MM-DD HH:MM)
const formatDateTime = (dateString: string) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// Format status label
const getStatusLabel = (status: string) => {
  return status === 'paid' ? t('paymentsList.paid') : t('paymentsList.unpaid')
}

// Open payment in new window
const openPaymentInNewWindow = (payment: any) => {
  if (payment.uuid && payment.hash) {
    // Use frontend URL for the payment page (Vue route)
    const url = `${window.location.origin}/payment/${payment.uuid}/${payment.hash}`
    console.log('Opening payment page:', url) // Debug log
    window.open(url, '_blank')
  } else {
    console.error('Payment missing uuid or hash:', payment) // Debug log
  }
  closeAllDropdowns()
}

// Checkbox functions
const toggleSelectAll = () => {
  if (selectAll.value) {
    // Select all current page payments
    payments.value.forEach(payment => {
      selectedPayments.value.add(payment.uuid)
    })
  } else {
    // Deselect all
    selectedPayments.value.clear()
  }
}

const togglePaymentSelection = (paymentUuid: string) => {
  if (selectedPayments.value.has(paymentUuid)) {
    selectedPayments.value.delete(paymentUuid)
  } else {
    selectedPayments.value.add(paymentUuid)
  }

  // Update select all checkbox state
  selectAll.value = payments.value.length > 0 &&
                    payments.value.every(p => selectedPayments.value.has(p.uuid))
}

const isPaymentSelected = (paymentUuid: string) => {
  return selectedPayments.value.has(paymentUuid)
}

// Dropdown state for action buttons
const openDropdown = ref<string | null>(null)

const toggleDropdown = (paymentUuid: string) => {
  openDropdown.value = openDropdown.value === paymentUuid ? null : paymentUuid
}

const closeAllDropdowns = () => {
  openDropdown.value = null
}

const isDropdownOpen = (paymentUuid: string) => {
  return openDropdown.value === paymentUuid
}

// Bulk actions dropdown
const showBulkActionsDropdown = ref(false)

const toggleBulkActionsDropdown = () => {
  showBulkActionsDropdown.value = !showBulkActionsDropdown.value
}

// Open send reminder modal
const openSendReminderModal = () => {
  singlePaymentForReminder.value = null
  showSendReminderModal.value = true
  showBulkActionsDropdown.value = false
}

// Open send reminder modal for single payment
const openSendReminderModalForPayment = (paymentUuid: string) => {
  singlePaymentForReminder.value = paymentUuid
  showSendReminderModal.value = true
  closeAllDropdowns()
}

const closeSendReminderModal = () => {
  showSendReminderModal.value = false
  singlePaymentForReminder.value = null
}

// Send payment reminder
const sendPaymentReminder = async (data: { message: string, sendEmail: boolean }) => {
  if (!clubUuid.value) return

  // Determine payment UUIDs - either from single payment or selected payments
  let paymentUuids: string[]

  if (singlePaymentForReminder.value) {
    // Single payment reminder
    paymentUuids = [singlePaymentForReminder.value]
  } else if (selectedPayments.value.size > 0) {
    // Bulk payment reminder
    paymentUuids = Array.from(selectedPayments.value)
  } else {
    return
  }

  try {
    const response = await paymentService.sendReminder(clubUuid.value, paymentUuids, data.message, data.sendEmail)
    toastStore.success(t('paymentsList.remindersSentSuccess'))

    // Clear selection if it was bulk reminder
    if (!singlePaymentForReminder.value) {
      selectedPayments.value.clear()
      selectAll.value = false
    }

    // Close modal
    closeSendReminderModal()
  } catch (error: any) {
    console.error('Error sending payment reminders:', error)
    const errorMessage = error.response?.data?.message || t('paymentsList.errorSendingReminders')
    toastStore.error(errorMessage)
  }
}

// Fetch payment reminder template
const fetchPaymentReminderTemplate = async () => {
  if (!clubUuid.value) return

  try {
    const response = await paymentTemplateService.getTemplate(clubUuid.value)
    paymentReminderTemplate.value = response.template || ''
  } catch (error: any) {
    console.error('Error fetching payment reminder template:', error)
    // Don't show error toast, just use default template
  }
}

// Close dropdowns when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('button') && !target.closest('.absolute')) {
    closeAllDropdowns()
    showBulkActionsDropdown.value = false
  }
}

// Delete payment
const openDeleteModal = (payment: any) => {
  if (payment.status === 'unpaid') {
    paymentToDelete.value = payment
    showDeleteModal.value = true
    closeAllDropdowns()
  }
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  paymentToDelete.value = null
}

const confirmDelete = async () => {
  if (!paymentToDelete.value || !clubUuid.value) return

  try {
    await paymentService.deletePayment(clubUuid.value, paymentToDelete.value.uuid)
    toastStore.success(t('paymentsList.paymentDeletedSuccessfully'))
    closeDeleteModal()
    fetchPayments()
  } catch (error: any) {
    console.error('Error deleting payment:', error)
    const errorMessage = error.response?.data?.message || t('paymentsList.errorDeletingPayment')
    toastStore.error(errorMessage)
  }
}

// Mark payment as paid
const openMarkAsPaidModal = (payment: any) => {
  if (payment.status === 'unpaid') {
    paymentToMarkAsPaid.value = payment
    showMarkAsPaidModal.value = true
    closeAllDropdowns()
  }
}

const closeMarkAsPaidModal = () => {
  showMarkAsPaidModal.value = false
  paymentToMarkAsPaid.value = null
}

const confirmMarkAsPaid = async () => {
  if (!paymentToMarkAsPaid.value || !clubUuid.value) return

  try {
    await paymentService.markAsPaid(clubUuid.value, paymentToMarkAsPaid.value.uuid)
    toastStore.success(t('paymentsList.paymentMarkedAsPaidSuccessfully'))
    closeMarkAsPaidModal()
    fetchPayments()
  } catch (error: any) {
    console.error('Error marking payment as paid:', error)
    const errorMessage = error.response?.data?.message || t('paymentsList.errorMarkingAsPaid')
    toastStore.error(errorMessage)
  }
}

// Bulk mark as paid
const bulkMarkAsPaid = async () => {
  if (!clubUuid.value || selectedPayments.value.size === 0) return

  const paymentUuids = Array.from(selectedPayments.value)

  try {
    const response = await paymentService.bulkMarkAsPaid(clubUuid.value, paymentUuids)
    toastStore.success(t('paymentsList.bulkMarkAsPaidSuccess'))

    // Clear selection
    selectedPayments.value.clear()
    selectAll.value = false

    // Refresh the list immediately
    fetchPayments()

    // Refresh again after 3 seconds to show updated status from queue
    setTimeout(() => {
      fetchPayments()
    }, 3000)
  } catch (error: any) {
    console.error('Error bulk marking payments as paid:', error)
    const errorMessage = error.response?.data?.message || t('paymentsList.errorBulkMarkingAsPaid')
    toastStore.error(errorMessage)
  }
}

// Bulk delete
const bulkDelete = async () => {
  if (!clubUuid.value || selectedPayments.value.size === 0) return

  const paymentUuids = Array.from(selectedPayments.value)

  try {
    const response = await paymentService.bulkDelete(clubUuid.value, paymentUuids)
    toastStore.success(t('paymentsList.bulkDeleteSuccess'))

    // Clear selection
    selectedPayments.value.clear()
    selectAll.value = false

    // Refresh the list immediately
    fetchPayments()

    // Refresh again after 3 seconds to show updated status from queue
    setTimeout(() => {
      fetchPayments()
    }, 3000)
  } catch (error: any) {
    console.error('Error bulk deleting payments:', error)
    const errorMessage = error.response?.data?.message || t('paymentsList.errorBulkDeleting')
    toastStore.error(errorMessage)
  }
}

onMounted(() => {
  if (!canViewPayments.value) {
    toastStore.error(t('paymentsList.noPermission'))
    router.push('/categories')
    return
  }

  initializeFiltersFromUrl()
  fetchPayments()

  // Only fetch reminder template if user has permission to send reminders
  if (canSendReminders.value) {
    fetchPaymentReminderTemplate()
  }

  // Add click outside listener
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Watch for route query changes
watch(() => route.query, () => {
  initializeFiltersFromUrl()
  fetchPayments()
}, { deep: true })
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('paymentsList.title') }}</h1>
        <div v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'" class="mt-2 flex flex-col gap-1">
          <router-link
            :to="`/club/${clubUuid}/payment-settings`"
            class="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/>
            </svg>
            {{ t('paymentsList.editPaymentSettings') }}
          </router-link>
          <router-link
            :to="`/club/${clubUuid}/payment-reminder-template`"
            class="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
            </svg>
            {{ t('paymentsList.editPaymentReminderTemplate') }}
          </router-link>
        </div>
      </div>

      <!-- Filters and Actions -->
      <div class="mb-4 flex flex-wrap justify-between items-center gap-2">
        <div class="flex gap-2">
          <button
            @click="showFilterModal = true"
            class="px-3 py-1.5 md:px-3 md:py-1.5 px-2 py-1 bg-white text-sm md:text-sm text-xs font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300 flex items-center gap-1 md:gap-1.5"
          >
            <svg class="w-3.5 h-3.5 md:w-3.5 md:h-3.5 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd"/>
            </svg>
            <span class="md:inline hidden">{{ t('paymentsList.filterPayments') }}</span>
            <span class="md:hidden inline">{{ t('paymentsList.filter') }}</span>
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col items-end gap-2 w-full md:w-auto ml-auto">
          <router-link
            v-if="canCreatePayments"
            to="/payments/add"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            {{ t('paymentsList.addPayment') }}
          </router-link>
          <router-link
            v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'"
            to="/recurring-payments"
            class="flex items-center gap-2 md:gap-2 gap-1 text-sm md:text-sm text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
            </svg>
            {{ t('paymentsList.recurringPayments') }}
          </router-link>
          <router-link
            v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'"
            :to="`/club/${clubUuid}/financial-stats`"
            class="flex items-center gap-2 md:gap-2 gap-1 text-sm md:text-sm text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
            </svg>
            {{ t('paymentsList.financialStats') }}
          </router-link>
        </div>
      </div>

      <!-- Active Filters -->
      <div v-if="hasActiveFilters" class="mb-4 flex flex-wrap gap-2">
        <div v-if="filters.search" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="removeFilter('search')" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('paymentsList.search') }}: "{{ filters.search }}"</span>
        </div>

        <div v-if="filters.status" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="removeFilter('status')" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('paymentsList.status') }}: {{ getStatusLabel(filters.status) }}</span>
        </div>

        <div v-if="filters.payment_date_from" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="removeFilter('payment_date_from')" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('paymentsList.paymentDateFrom') }}: {{ formatDate(filters.payment_date_from) }}</span>
        </div>

        <div v-if="filters.payment_date_to" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="removeFilter('payment_date_to')" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('paymentsList.paymentDateTo') }}: {{ formatDate(filters.payment_date_to) }}</span>
        </div>

        <div v-if="filters.due_date_from" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="removeFilter('due_date_from')" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('paymentsList.dueDateFrom') }}: {{ formatDate(filters.due_date_from) }}</span>
        </div>

        <div v-if="filters.due_date_to" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="removeFilter('due_date_to')" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('paymentsList.dueDateTo') }}: {{ formatDate(filters.due_date_to) }}</span>
        </div>

        <button @click="clearAllFilters" class="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 font-medium">
          {{ t('paymentsList.clearAllFilters') }}
        </button>
      </div>

      <!-- Mark as Paid Confirmation Modal -->
      <div v-if="showMarkAsPaidModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeMarkAsPaidModal">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('paymentsList.markAsPaidTitle') }}</h2>
          <p class="text-gray-700 mb-6">
            {{ t('paymentsList.markAsPaidConfirm') }}
            <strong>{{ paymentToMarkAsPaid?.name }}</strong>
            {{ t('paymentsList.forPlayer') }}
            <strong>{{ paymentToMarkAsPaid?.player?.first_name }} {{ paymentToMarkAsPaid?.player?.last_name }}</strong>?
          </p>
          <div class="flex gap-2 justify-end">
            <button
              @click="closeMarkAsPaidModal"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            >
              {{ t('paymentsList.cancel') }}
            </button>
            <button
              @click="confirmMarkAsPaid"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700"
            >
              {{ t('paymentsList.markAsPaidSingle') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeDeleteModal">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('paymentsList.deletePaymentTitle') }}</h2>
          <p class="text-gray-700 mb-6">
            {{ t('paymentsList.deletePaymentConfirm') }}
            <strong>{{ paymentToDelete?.name }}</strong>
            {{ t('paymentsList.forPlayer') }}
            <strong>{{ paymentToDelete?.player?.first_name }} {{ paymentToDelete?.player?.last_name }}</strong>?
          </p>
          <div class="flex gap-2 justify-end">
            <button
              @click="closeDeleteModal"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            >
              {{ t('paymentsList.cancel') }}
            </button>
            <button
              @click="confirmDelete"
              class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700"
            >
              {{ t('paymentsList.delete') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Filter Modal -->
      <div v-if="showFilterModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="showFilterModal = false">
        <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('paymentsList.filterModalTitle') }}</h2>

            <div class="grid lg:grid-cols-2 gap-4">
              <!-- Search -->
              <div class="lg:col-span-2">
                <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('paymentsList.searchLabel') }}</label>
                <input
                  v-model="filters.search"
                  type="text"
                  :placeholder="t('paymentsList.searchPlaceholder')"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Status -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('paymentsList.statusLabel') }}</label>
                <select v-model="filters.status" class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-white focus:outline-none focus:border-blue-600">
                  <option value="">{{ t('paymentsList.all') }}</option>
                  <option value="paid">{{ t('paymentsList.paid') }}</option>
                  <option value="unpaid">{{ t('paymentsList.unpaid') }}</option>
                </select>
              </div>

              <div></div>

              <!-- Payment date from -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('paymentsList.paymentDateFromLabel') }}</label>
                <input v-model="filters.payment_date_from" type="date" class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600">
              </div>

              <!-- Payment date to -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('paymentsList.paymentDateToLabel') }}</label>
                <input v-model="filters.payment_date_to" type="date" class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600">
              </div>

              <!-- Due date from -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('paymentsList.dueDateFromLabel') }}</label>
                <input v-model="filters.due_date_from" type="date" class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600">
              </div>

              <!-- Due date to -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('paymentsList.dueDateToLabel') }}</label>
                <input v-model="filters.due_date_to" type="date" class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600">
              </div>
            </div>

            <!-- Buttons -->
            <div class="mt-6 flex gap-2 justify-end">
              <button @click="showFilterModal = false" class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300">
                {{ t('paymentsList.close') }}
              </button>
              <button @click="applyFilters" class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700">
                {{ t('paymentsList.applyFilters') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Bulk Actions Bar -->
      <div v-if="showBulkActions" class="mb-4 flex flex-col gap-3 bg-blue-50 border-2 border-blue-300 rounded p-3">
        <div class="flex items-center gap-3">
          <div class="relative">
            <button @click="toggleBulkActionsDropdown" class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 flex items-center gap-2">
              <span>{{ t('paymentsList.optionsForSelected') }} ({{ selectedPayments.size }})</span>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="showBulkActionsDropdown" class="absolute left-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-lg z-50">
              <button @click="bulkMarkAsPaid" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                {{ t('paymentsList.markAsPaid') }}
              </button>
              <button @click="bulkDelete" class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                {{ t('paymentsList.deletePayments') }}
              </button>
              <button v-if="canSendReminders" @click="openSendReminderModal" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                {{ t('paymentsList.sendReminder') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('paymentsList.loading') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-if="!loading" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm">
        <div style="overflow-x: clip; overflow-y: visible;">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th class="px-2 py-2 text-center w-8">
                  <input type="checkbox" v-model="selectAll" @change="toggleSelectAll" class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500">
                </th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('paymentsList.name') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('paymentsList.player') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('paymentsList.amount') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700 lg:hidden">{{ t('paymentsList.date') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('paymentsList.paymentDate') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('paymentsList.dueDate') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('paymentsList.paidDate') }}</th>
                <th class="px-2 py-2 text-left font-semibold text-gray-700 hidden md:table-cell">{{ t('paymentsList.statusColumn') }}</th>
                <th class="px-2 py-2 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-if="payments.length === 0">
                <td colspan="10" class="px-2 py-8 text-center text-gray-500">
                  {{ t('paymentsList.noPayments') }}
                </td>
              </tr>
              <tr v-for="payment in payments" :key="payment.uuid" class="hover:bg-gray-50">
                <td class="px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    :checked="isPaymentSelected(payment.uuid)"
                    @change="togglePaymentSelection(payment.uuid)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  >
                </td>
                <td class="px-2 py-2 text-gray-900 font-medium">{{ payment.name }}</td>
                <td class="px-2 py-2">
                  <router-link :to="`/players/${payment.player?.uuid}`" class="text-blue-600 hover:underline">
                    {{ payment.player?.first_name }} {{ payment.player?.last_name }}
                  </router-link>
                </td>
                <td class="px-2 py-2 font-medium" :class="(payment.status === 'paid' || payment.paid_date) ? 'text-green-600 md:text-gray-900' : 'text-red-600 md:text-gray-900'">
                  {{ payment.amount }} {{ payment.currency || 'PLN' }}
                </td>
                <td class="px-2 py-2 text-gray-700 lg:hidden">
                  <div>{{ formatDate(payment.payment_date) }}</div>
                  <div class="text-xs text-gray-500">{{ t('paymentsList.deadline') }}: {{ formatDate(payment.due_date) }}</div>
                </td>
                <td class="px-2 py-2 text-gray-700 hidden lg:table-cell">{{ formatDate(payment.payment_date) }}</td>
                <td class="px-2 py-2 text-gray-700 hidden lg:table-cell">{{ formatDate(payment.due_date) }}</td>
                <td class="px-2 py-2 text-gray-700 hidden lg:table-cell">{{ formatDateTime(payment.paid_date) }}</td>
                <td class="px-2 py-2 hidden md:table-cell">
                  <span v-if="payment.status === 'paid' || payment.paid_date" class="px-1 py-0.5 bg-green-100 text-green-800 text-[10px] font-medium rounded border border-green-300 lg:hidden md:inline">{{ t('paymentsList.paidShort') }}</span>
                  <span v-if="payment.status === 'paid' || payment.paid_date" class="hidden lg:inline px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded border border-green-300">
                    {{ t('paymentsList.paid') }}
                  </span>
                  <span v-if="payment.status !== 'paid' && !payment.paid_date" class="px-1 py-0.5 bg-red-100 text-red-800 text-[10px] font-medium rounded border border-red-300 lg:hidden md:inline">{{ t('paymentsList.unpaidShort') }}</span>
                  <span v-if="payment.status !== 'paid' && !payment.paid_date" class="hidden lg:inline px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded border border-red-300">
                    {{ t('paymentsList.unpaid') }}
                  </span>
                </td>
                <td class="px-2 py-2 sticky right-0 bg-inherit text-right" style="position: relative;">
                  <button @click.stop="toggleDropdown(payment.uuid)" class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300">
                    {{ t('paymentsList.actions') }}
                  </button>
                  <div
                    v-show="isDropdownOpen(payment.uuid)"
                    class="absolute right-[15px] mt-1 min-w-[160px] bg-white border-2 border-gray-300 rounded shadow-lg z-[9999]"
                    style="top: 85%;"
                  >
                    <button
                      @click="openPaymentInNewWindow(payment)"
                      class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {{ t('paymentsList.goTo') }}
                    </button>
                    <router-link
                      v-if="payment.status === 'unpaid' && currentRole !== 'PLAYER'"
                      :to="`/payments/${payment.uuid}/edit`"
                      class="block w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {{ t('paymentsList.edit') }}
                    </router-link>
                    <button
                      v-if="payment.status === 'unpaid' && currentRole !== 'PLAYER'"
                      @click="openMarkAsPaidModal(payment)"
                      class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {{ t('paymentsList.markAsPaidSingle') }}
                    </button>
                    <button
                      v-if="payment.status === 'unpaid' && canSendReminders"
                      @click="openSendReminderModalForPayment(payment.uuid)"
                      class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {{ t('paymentsList.sendNotification') }}
                    </button>
                    <button
                      v-if="payment.status === 'unpaid' && currentRole !== 'PLAYER'"
                      @click="openDeleteModal(payment)"
                      class="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-gray-100"
                    >
                      {{ t('paymentsList.delete') }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile Accordion -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div v-if="payments.length === 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center text-gray-500">
          {{ t('paymentsList.noPayments') }}
        </div>
        <div v-for="payment in payments" :key="payment.uuid" class="bg-white border-2 border-gray-300 rounded shadow-sm">
          <!-- Accordion Header -->
          <div class="flex items-center p-3">
            <input
              type="checkbox"
              :checked="isPaymentSelected(payment.uuid)"
              @change="togglePaymentSelection(payment.uuid)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
              @click.stop
            >
            <div class="flex-1 min-w-0 cursor-pointer" @click="togglePaymentAccordion(payment.uuid)">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0">
                  <div class="text-xs sm:text-sm font-medium text-gray-900">{{ payment.name }}</div>
                  <div class="text-[10px] sm:text-xs text-gray-600">{{ payment.player?.first_name }} {{ payment.player?.last_name }}</div>
                </div>
                <div class="flex items-center gap-2 ml-2">
                  <div class="text-right">
                    <div class="text-sm font-medium" :class="(payment.status === 'paid' || payment.paid_date) ? 'text-green-600' : 'text-red-600'">
                      {{ payment.amount }} {{ payment.currency || 'PLN' }}
                    </div>
                    <div class="text-xs text-gray-600">{{ formatDate(payment.due_date) }}</div>
                  </div>
                  <div style="position: relative;">
                    <button @click.stop="toggleDropdown(payment.uuid)" class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300">
                      {{ t('paymentsList.actions') }}
                    </button>
                    <div
                      v-show="isDropdownOpen(payment.uuid)"
                      class="absolute right-[15px] mt-1 min-w-[160px] bg-white border-2 border-gray-300 rounded shadow-lg z-[9999]"
                      style="top: 85%;"
                      @click.stop
                    >
                      <button
                        @click="openPaymentInNewWindow(payment)"
                        class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        {{ t('paymentsList.goTo') }}
                      </button>
                      <router-link
                        v-if="payment.status === 'unpaid' && currentRole !== 'PLAYER'"
                        :to="`/payments/${payment.uuid}/edit`"
                        class="block w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        {{ t('paymentsList.edit') }}
                      </router-link>
                      <button
                        v-if="payment.status === 'unpaid' && currentRole !== 'PLAYER'"
                        @click="openMarkAsPaidModal(payment)"
                        class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        {{ t('paymentsList.markAsPaidSingle') }}
                      </button>
                      <button
                        v-if="payment.status === 'unpaid' && canSendReminders"
                        @click="openSendReminderModalForPayment(payment.uuid)"
                        class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        {{ t('paymentsList.sendNotification') }}
                      </button>
                      <button
                        v-if="payment.status === 'unpaid' && currentRole !== 'PLAYER'"
                        @click="openDeleteModal(payment)"
                        class="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-gray-100"
                      >
                        {{ t('paymentsList.delete') }}
                      </button>
                    </div>
                  </div>
                  <svg
                    :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': isPaymentExpanded(payment.uuid) }]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <!-- Accordion Content -->
          <div v-show="isPaymentExpanded(payment.uuid)" class="border-t-2 border-gray-300 p-3">
            <div class="space-y-2 text-xs">
              <div>
                <span class="font-semibold text-gray-700">{{ t('paymentsList.paymentDate') }}:</span>
                <span class="text-gray-600">{{ formatDate(payment.payment_date) }}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-700">{{ t('paymentsList.paidDate') }}:</span>
                <span class="text-gray-600">{{ formatDateTime(payment.paid_date) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && pagination.total > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('paymentsList.displaying') }} {{ pagination.from }} - {{ pagination.to }} {{ t('paymentsList.of') }} {{ pagination.total }} {{ t('paymentsList.payments') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('paymentsList.perPage') }}</label>
            <select id="perPage" v-model.number="pagination.perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="pagination.currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === pagination.currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="pagination.currentPage === pagination.lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>

      <!-- Send Payment Reminder Modal -->
      <SendPaymentReminderModal
        :show="showSendReminderModal"
        :selected-payments-count="singlePaymentForReminder ? 1 : selectedPayments.size"
        :default-template="paymentReminderTemplate"
        @close="closeSendReminderModal"
        @send="sendPaymentReminder"
      />
    </div>
  </main>
</template>
